import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDepartments, getKb, submitTicket } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { matchArticles } from "../lib/kbSearch";
import { Alert, Spinner } from "../components/States";

/** The portal serves the whole campus — every requester type, not just students. */
const AFFILIATIONS = ["Student", "Faculty", "Staff", "Parent / Family", "Applicant", "Alumni", "Other"];

/** Debounce a string value (deflection lookups shouldn't run per keystroke). */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/** "Submit a request" form (subject + description) → POST /tickets, then route to
 *  the created ticket's detail view. DEFLECTION: as the subject is typed we match
 *  it (debounced, client-side) against the published KB and suggest articles that
 *  might already answer the question — submitting always remains possible. */
export function NewTicketPage() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [department, setDepartment] = useState("");
  const { run, pending, error } = useMutation(submitTicket);

  // Departments (configured by the tenant; routes to the right office's team).
  // Failure just hides the select — submission must never block on it.
  const depts = useApi((signal) => getDepartments(signal), []);
  const departments = depts.data?.departments ?? [];

  // One KB fetch for the whole page; matching is client-side over the list. A
  // failed fetch silently disables suggestions — deflection must never block.
  const kb = useApi((signal) => getKb(undefined, signal), []);
  const debouncedSubject = useDebounced(subject, 300);
  const suggestions = useMemo(
    () => matchArticles(kb.data?.articles ?? [], debouncedSubject, 5),
    [kb.data, debouncedSubject],
  );

  const canSubmit = subject.trim().length > 0 && body.trim().length > 0;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const res = await run({
      subject: subject.trim(),
      body: body.trim(),
      ...(affiliation ? { affiliation } : {}),
      ...(department ? { department } : {}),
    });
    if (res?.ticket) {
      navigate(`/tickets/${encodeURIComponent(res.ticket.id)}`, { replace: true });
    }
  }

  return (
    <div className="new-ticket">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/tickets">My requests</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">New request</span>
      </nav>

      <div className="page-head">
        <div>
          <h1>Submit a request</h1>
          <p className="sub">Tell us what’s going on and we’ll follow up by email.</p>
        </div>
      </div>

      <form className="card form-card" onSubmit={onSubmit} noValidate>
        <div className="card-body">
          {error ? <Alert kind="error">{error}</Alert> : null}

          <label className="field">
            <span className="label">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              autoFocus
              placeholder="Brief summary of your request"
              required
            />
          </label>

          {/* The portal serves every campus user type — capture who's asking and
              which office it's for (department routes to the right team). */}
          <div className="field-row" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <label className="field" style={{ flex: 1, minWidth: 180 }}>
              <span className="label">I am a…</span>
              <select value={affiliation} onChange={(e) => setAffiliation(e.target.value)}>
                <option value="">Select (optional)</option>
                {AFFILIATIONS.map((a) => (
                  <option key={a} value={a.toLowerCase()}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            {departments.length > 0 ? (
              <label className="field" style={{ flex: 1, minWidth: 180 }}>
                <span className="label">Which office is this for?</span>
                <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                  <option value="">Not sure (optional)</option>
                  {departments.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <label className="field">
            <span className="label">How can we help?</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Describe your question or issue in as much detail as you can."
              required
            />
            <span className="hint">
              Basic formatting (markdown) is supported. Don’t include passwords or other secrets.
            </span>
          </label>

          {/* Deflection: KB titles matching the subject, above the submit button. */}
          {suggestions.length > 0 ? (
            <aside className="deflect" aria-live="polite" aria-label="Suggested articles">
              <p className="deflect-title">These articles might help</p>
              <ul className="deflect-list">
                {suggestions.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/kb/${encodeURIComponent(a.slug)}`}
                      className="deflect-row"
                      target="_blank"
                      rel="noopener"
                    >
                      <span className="deflect-name">{a.title}</span>
                      <span className="kb-article-arrow" aria-hidden="true">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="hint">
                Opens in a new tab — your draft stays right here.
              </p>
            </aside>
          ) : null}

          <div className="btn-row">
            <button className="btn primary" type="submit" disabled={pending || !canSubmit}>
              {pending ? <Spinner label="Submitting…" /> : "Submit request"}
            </button>
            <Link to="/tickets" className="btn ghost">
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
